// A match on record: who played, with which decks, and every action in order.
//
// The shuffles and dice all come from one seed, so the same actions always give the same game.
// That one record does three jobs:
//   - a saved game: load it and the table is rebuilt exactly, ready to carry on
//   - a replay: play the actions back one at a time
//   - (later) the server's memory of a game two players can come back to
import type { CardDb } from './cards.ts';
import type { TokenDb } from './tokens.ts';
import { Game, type Action, type GameResult, type Seat } from './engine.ts';

export const MATCH_FORMAT = 'arellix-match';
export const MATCH_VERSION = 1;
/** The ending of a match file's name. */
export const MATCH_EXTENSION = '.arellix';

/** One thing a player did: who, when (milliseconds since the match began) and what. */
export interface MatchAction {
  s: Seat;
  t: number;
  a: Action;
}

export interface MatchFile {
  format: typeof MATCH_FORMAT;
  version: number;
  savedAt: string; // when the file was written
  finished: boolean; // false = a game saved part-way, which can be resumed
  fingerprint: string; // of the card list, token list and rules it was played with
  mode: 'solo' | 'versus';
  teams?: boolean; // a 2v2 game: four names and decks
  bots?: Seat[]; // seats the computer played
  botLevels?: Partial<Record<number, 'easy' | 'skilled' | 'expert'>>; // how well each bot chose (Martin, Practice Buddy, Creator)
  tutorial?: boolean; // a tutorial game against Martin
  names: string[];
  decks: number[][];
  seed: number;
  actions: MatchAction[];
  result: GameResult | null;
}

/** A small repeatable random number generator: the same seed always gives the same numbers. */
export function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A short code that changes whenever the text it was made from changes. */
export function fingerprintOf(...parts: string[]): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x1b873593;
  for (const part of parts) {
    for (let i = 0; i < part.length; i++) {
      const c = part.charCodeAt(i);
      h1 = Math.imul(h1 ^ c, 0x01000193);
      h2 = Math.imul(h2 + c, 0x85ebca6b) ^ (h2 >>> 13);
    }
    h1 = Math.imul(h1 ^ 0xff, 0x01000193);
  }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

export interface MatchSetup {
  mode: 'solo' | 'versus';
  teams?: boolean;
  bots?: Seat[];
  botLevels?: Partial<Record<number, 'easy' | 'skilled' | 'expert'>>;
  tutorial?: boolean;
  names: string[];
  decks: number[][];
  seed: number;
  fingerprint: string;
}

export class Match {
  readonly game: Game;
  readonly setup: MatchSetup;
  readonly actions: MatchAction[] = [];

  constructor(cards: CardDb, tokens: TokenDb, setup: MatchSetup) {
    this.setup = setup;
    this.game = new Game(cards, tokens, { mode: setup.mode, teams: !!setup.teams, names: setup.names, decks: setup.decks, rng: seededRng(setup.seed), ...(setup.bots?.length ? { bots: setup.bots, ...(setup.botLevels ? { botLevels: setup.botLevels } : {}) } : {}), ...(setup.tutorial ? { tutorial: true } : {}) });
  }

  /** Applies an action and, if it worked, puts it on record. `t` = milliseconds since the match began. */
  apply(seat: Seat, action: Action, t: number): string | null {
    const error = this.game.apply(seat, action);
    if (!error) this.actions.push({ s: seat, t: Math.max(0, Math.round(t)), a: action });
    return error;
  }

  /** How long the match has run, by its last action. */
  get length() {
    return this.actions.at(-1)?.t ?? 0;
  }

  toFile(): MatchFile {
    return {
      format: MATCH_FORMAT,
      version: MATCH_VERSION,
      savedAt: new Date().toISOString(),
      finished: this.game.state.result !== null,
      fingerprint: this.setup.fingerprint,
      mode: this.setup.mode,
      ...(this.setup.teams ? { teams: true } : {}),
      ...(this.setup.bots?.length ? { bots: [...this.setup.bots], ...(this.setup.botLevels ? { botLevels: { ...this.setup.botLevels } } : {}) } : {}),
      ...(this.setup.tutorial ? { tutorial: true } : {}),
      names: [...this.setup.names],
      decks: this.setup.decks.map((d) => [...d]),
      seed: this.setup.seed,
      actions: this.actions.map((x) => ({ ...x })),
      result: this.game.state.result ? { ...this.game.state.result } : null,
    };
  }

  /**
   * Rebuilds a match from its file by playing the recorded actions again, all of them or only the
   * first `upTo`. `brokeAt` is the number of the first action that no longer worked (the card list or
   * the rules have changed since it was recorded), or null if everything played back.
   */
  static load(file: MatchFile, cards: CardDb, tokens: TokenDb, upTo = file.actions.length): { match: Match; brokeAt: number | null } {
    const match = new Match(cards, tokens, { mode: file.mode, teams: !!file.teams, ...(file.bots?.length ? { bots: file.bots, ...(file.botLevels ? { botLevels: file.botLevels } : {}) } : {}), ...(file.tutorial ? { tutorial: true } : {}), names: file.names, decks: file.decks, seed: file.seed, fingerprint: file.fingerprint });
    const n = Math.max(0, Math.min(upTo, file.actions.length));
    for (let i = 0; i < n; i++) {
      const x = file.actions[i];
      if (match.apply(x.s, x.a, x.t)) return { match, brokeAt: i };
    }
    return { match, brokeAt: null };
  }
}

/** Reads the text of a match file. Returns the match, or a sentence saying what is wrong with it. */
export function parseMatchFile(text: string): MatchFile | string {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return 'That file isn’t an Arellix game or replay (it could not be read).';
  }
  const f = data as Partial<MatchFile>;
  if (!f || f.format !== MATCH_FORMAT) return 'That file isn’t an Arellix game or replay.';
  if (typeof f.version !== 'number' || f.version > MATCH_VERSION) return 'That file was saved by a newer version of the game.';
  const ok =
    (f.mode === 'solo' || f.mode === 'versus') &&
    Array.isArray(f.names) && f.names.every((n) => typeof n === 'string') &&
    Array.isArray(f.decks) && f.decks.every((d) => Array.isArray(d) && d.every((n) => Number.isInteger(n))) &&
    typeof f.seed === 'number' &&
    Array.isArray(f.actions) && f.actions.every((x) => x && [0, 1, 2, 3].includes(x.s) && typeof x.t === 'number' && !!x.a && typeof x.a.type === 'string');
  if (!ok) return 'That file is damaged: some of the game is missing from it.';
  return { ...(f as MatchFile), finished: !!f.finished, fingerprint: String(f.fingerprint ?? ''), result: f.result ?? null };
}

/** A file name for a match: who played and when it was saved. */
export function matchFileName(file: MatchFile): string {
  const slug = (n: string) => n.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'player';
  // 2v2: "ana-cam-vs-ben-dee"
  const who = file.teams ? [0, 1].map((t) => file.names.filter((_, i) => i % 2 === t).map(slug).join('-')).join('-vs-') : file.names.map(slug).join('-vs-');
  const when = file.savedAt.slice(0, 16).replace(/[:T]/g, '-');
  return `arellix-${file.finished ? 'replay' : 'saved-game'}-${who}-${when}${MATCH_EXTENSION}`;
}

/** "12:05" */
export function clock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(s / 60);
  return (m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}` : String(m)) + ':' + String(s % 60).padStart(2, '0');
}
